/**
 * Custom hook for managing Create Chat form state and submission
 * Provides form validation, state management, and chat creation logic
 */

import {
  CreateChatFormData,
  CreateChatType,
  CreatedContext,
} from '@choco-admin/types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../contexts/NotificationContext';
import { usePuppyMode } from '../../../hooks';

import { useCreateChat } from './useCreateChat';

// Default form values
const defaultFormValues: CreateChatFormData = {
  // Participants
  buyer: null,
  supplier: null,
  buyerUsers: [],
  supplierUsers: [],
  deselectedOrderPreferences: [],

  // Settings
  chatName: '',
  restaurantName: '',
  deliveryAddress: '',
  customerNumber: '',
  additionalEmails: '',
  chatType: { value: CreateChatType.ORDERING, label: 'Ordering' },
  subCatalog: [],
  createdContext: { value: CreatedContext.ADMIN, label: 'Admin Created' },
  customerLevelMovAmount: null,
  customerLevelMovCurrency: null,
  visibleChatPrices: false,
  isPriceOverrideEnabled: false,

  // Delivery settings
  hasCustomChatDeliverySettings: false,
  deliveryDays: [],
  cutoffType: 'NoCutoff' as any,
  cutoffTimes: undefined,
};

export interface UseCreateChatFormResponse extends ReturnType<
  typeof useForm<CreateChatFormData>
> {
  // Additional data and actions
  formData: CreateChatFormData;
  onSubmit: () => void;

  // Loading states
  isLoading: boolean;
  error: Error | null;
}

export const useCreateChatForm = (
  initialValues: Partial<CreateChatFormData> = {}
): UseCreateChatFormResponse => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { filterMode } = usePuppyMode();
  const isPuppyMode = filterMode === 'puppy';
  const { createChat, isLoading, error } = useCreateChat({
    onSuccess: (chat) => {
      addNotification({
        title: 'Success',
        message: 'Chat created successfully',
        type: 'success',
      });
      navigate(`/chats/${chat.id}`);
    },
    onError: (createError) => {
      addNotification({
        title: 'Error',
        message: createError.message || 'Failed to create chat',
        type: 'error',
      });
    },
  });

  // Merge default values with initial values
  const formDefaultValues = { ...defaultFormValues, ...initialValues };

  const formMethods = useForm<CreateChatFormData>({
    defaultValues: formDefaultValues,
    // resolver: yupResolver(createChatSchema), // Temporarily disabled for merge conflict resolution
    mode: 'onChange',
  });

  const { handleSubmit, watch, setValue, reset } = formMethods;
  const formData = watch();

  // Update form values when initial values change (e.g., when duplicate data is loaded)
  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      reset(formDefaultValues);
    }
  }, [initialValues, formDefaultValues, reset]);

  // Reset buyer/supplier selections when puppy mode changes (but not if we have initial values)
  useEffect(() => {
    // Don't reset if we have initial values (e.g., from duplicate data)
    if (Object.keys(initialValues).length === 0) {
      setValue('buyer', null);
      setValue('supplier', null);
      setValue('buyerUsers', []);
      setValue('supplierUsers', []);
    }
  }, [isPuppyMode, setValue, initialValues]);

  const onSubmit = handleSubmit(async (data: CreateChatFormData) => {
    try {
      await createChat(data);
    } catch (err) {
      // Error handling is done in useCreateChat hook
      // Error handling is done in useCreateChat hook
    }
  });

  return {
    // All form methods for FormProvider compatibility
    ...formMethods,

    // Additional data and actions
    formData,
    onSubmit,

    // Loading states
    isLoading: isLoading || formMethods.formState.isSubmitting,
    error,
  };
};
